/**
 * Trifold Card Print helper — opens a new window with 3 stacked panels
 * matching the shop's preconfigured trifold enclosure card paper:
 *   Panel 1 (top)    — card message
 *   Panel 2 (middle) — recipient name, address, phone
 *   Panel 3 (bottom) — shop logo + shop name/address
 * Text prints right-side-up in all panels; the user's paper preset handles
 * size and margins.
 */
import { toast } from 'sonner';
import { API } from '@/config/constants';
import { escapeHtml as esc } from '@/utils/htmlEscape';
import { scheduleAutomaticPrint } from '@/utils/automaticPrint';

// The popup window's size is what the auto-fit script (below) measures
// against — it must match the real trifold card stock's physical
// proportions, not an arbitrary screen size, or auto-fit calculates
// line-wrapping against the wrong width and the message can overflow the
// actual printed card. Card stock measures 4-1/8" x 9-2/5"; 96 CSS px/inch
// is the standard browser reference density.
const CARD_WIDTH_IN = 4.125;
const CARD_HEIGHT_IN = 9.4;
const PX_PER_IN = 96;
const POPUP_WIDTH = Math.round(CARD_WIDTH_IN * PX_PER_IN);
const POPUP_HEIGHT = Math.round(CARD_HEIGHT_IN * PX_PER_IN);
export const TRIFOLD_POPUP_FEATURES = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT}`;

export function printTrifoldCard(order, shop, {
  includeOrderNumber = true, popup, isCurrent = () => true,
  onSettled = () => {}, brandingFallback = false,
} = {}) {
  if (!order) {
    onSettled();
    return { cancelAutoPrint: () => {} };
  }

  const message = order.card_message || '';
  const recipientLine1 = esc(order.recipient_name || '');
  const addr1 = esc(order.recipient_street_address || '');
  const addr2 = esc(order.recipient_street_address_2 || '');
  const cityStateZip = esc(
    [order.recipient_city, order.recipient_state, order.recipient_zip_code]
      .filter(Boolean).join(', ')
  );
  const phone = esc(order.recipient_phone || '');
  // The day's sequence number with leading zeros stripped: the part after
  // the last '-' in internal_order_number, as an integer (e.g. 20260630-047
  // -> "47", ...-001 -> "1"). Omitted when there's no number, or when the
  // caller opts out via includeOrderNumber:false (All Orders → Print Options).
  // This rule must stay identical to the order-document stamp feature so the
  // two printed numbers always match.
  const ionSeg = String(order.internal_order_number || '').split('-').pop();
  const seqNum = parseInt(ionSeg, 10);
  const orderNumDisplay = includeOrderNumber && !Number.isNaN(seqNum) ? String(seqNum) : '';
  const shopName = esc((shop && shop.name) || 'Las Vegas Bouquet');
  const shopAddress = esc((shop && shop.address) || '');
  const logoSrc = shop && shop.logo_id
    ? `${API}/public/shop-logo?v=${encodeURIComponent(shop.logo_id)}`
    : null;

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Trifold Card — ${esc(order.internal_order_number || order.order_number || '')}</title>
<style>
  @page { margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; color: #111;
    font-family: Georgia, 'Times New Roman', serif; }
  .page { display: flex; flex-direction: column; width: 100%; height: 100vh; }
  .panel {
    flex: 1 1 0;
    box-sizing: border-box;
    padding: 0.4in 0.5in;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    overflow: hidden;
    page-break-inside: avoid;
  }
  .panel + .panel { border-top: 1px dashed #ccc; }
  .msg-wrap {
    /* Auto-fit JS uses this wrapper to measure available space. The small
     * inner padding gives the shrunken text visual breathing room from the
     * panel's dashed divider in print preview without changing the outer
     * panel padding (which is shared with the recipient & shop panels). */
    width: 100%;
    height: 100%;
    padding: 0.03in 0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
  }
  .msg { font-style: italic; font-size: 22pt; line-height: 1.35;
    white-space: pre-wrap; max-width: 90%; }
  .recipient-name { font-size: 20pt; font-weight: 700; letter-spacing: 0.5px;
    margin-bottom: 0.18in; }
  .recipient-address { font-size: 14pt; line-height: 1.45; }
  .recipient-phone { font-size: 13pt; margin-top: 0.18in; color: #333; }
  .panel[data-panel="recipient"] { position: relative; }
  .recipient-ordernum { position: absolute; right: 0.2in; bottom: 0.15in;
    font-size: 12pt; font-weight: 700; color: #111; line-height: 1; }
  .shop-logo { max-height: 1.1in; max-width: 3in; object-fit: contain;
    margin-bottom: 0.1in; }
  .shop-name { font-size: 16pt; font-weight: 700; letter-spacing: 1px;
    text-transform: uppercase; }
  .shop-address { font-size: 11pt; color: #444; margin-top: 0.08in; }
  .branding-notice { position: fixed; top: 0; left: 0; right: 0; z-index: 1;
    padding: 4px; background: #fff7ed; font: 12px Arial, sans-serif; }
  @media print {
    .branding-notice { display: none; }
    .panel + .panel { border-top: 0; }
    html, body, .page { height: 100%; }
  }
</style>
</head>
<body>
  ${brandingFallback ? '<aside class="branding-notice">Shop branding was unavailable. Default branding is shown.</aside>' : ''}
  <div class="page">
    <section class="panel" data-panel="message">
      <div class="msg-wrap">
        <div class="msg">${esc(message) || '&nbsp;'}</div>
      </div>
    </section>
    <section class="panel" data-panel="recipient">
      <div class="recipient-name">${recipientLine1 || '&nbsp;'}</div>
      <div class="recipient-address">
        ${addr1}${addr2 ? '<br/>' + addr2 : ''}
        ${cityStateZip ? '<br/>' + cityStateZip : ''}
      </div>
      ${phone ? `<div class="recipient-phone">${phone}</div>` : ''}
      ${orderNumDisplay ? `<div class="recipient-ordernum">${orderNumDisplay}</div>` : ''}
    </section>
    <section class="panel" data-panel="shop">
      ${logoSrc ? `<img class="shop-logo" src="${logoSrc}" alt="${shopName}" crossorigin="anonymous" />` : ''}
      <div class="shop-name">${shopName}</div>
      ${shopAddress ? `<div class="shop-address">${shopAddress}</div>` : ''}
    </section>
  </div>
</body>
</html>`;

  const w = popup === undefined ? window.open('', '_blank', TRIFOLD_POPUP_FEATURES) : popup;
  if (!w) {
    toast.error('Pop-ups blocked — allow pop-ups to print');
    onSettled();
    return { cancelAutoPrint: () => {} };
  }
  if (w.closed || !isCurrent()) {
    onSettled();
    return { cancelAutoPrint: () => {} };
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  return scheduleAutomaticPrint({
    popup: w, isCurrent, onSettled,
    image: w.document.querySelector('.shop-logo'),
    afterFitDelay: 80,
    beforePrint: () => {
      const msg = w.document.querySelector('.msg');
      const wrap = w.document.querySelector('.msg-wrap');
      if (!msg || !wrap) return;
      // Preserve the 22pt start and 10pt readability floor. Measure each step.
      let size = 22;
      msg.style.fontSize = `${size}pt`;
      while (size > 10 && msg.scrollHeight > wrap.clientHeight) {
        size -= 1;
        msg.style.fontSize = `${size}pt`;
      }
    },
  });
}
